import React, { ChangeEvent, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  // TextField,
  Typography,
  Tooltip, FormControl
} from '@mui/material';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styles from "../EntityEditor.styles";
// import StyledTextField from "../../styled-components/StyledTextField";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch } from "react-redux";
import { LinkTextField } from '../../link-text-field/LinkTextField';
import AlertService from '../../../services/notification.service';
import { toPlatform, type SocialPlatform } from '../../helpers/validator';
import { StyledInputLabel, StyledSelect } from '../../styled-components/StyledSelect';
import { SelectChangeEvent } from '@mui/material/Select';

interface SocialDetails {
  type: SocialPlatform | undefined;
  url: string;
  index?: number;
  id?: string;
  modified?: boolean;
}

const initialEditModeState = {
  isEdit: false,
  index: -1,
};

const socialLinkOptions: ReadonlyArray<{label: string; value: SocialPlatform}> = [
  {
    label: "Facebook",
    value: "facebook",
  },
  {
    label: "Twitter",
    value: "twitter",
  },
  {
    label: "LinkedIn",
    value: "linkedin",
  },
];

const initialSocialDetails: SocialDetails = {
  type: undefined,
  url: "",
  index: -1,
  id: "",
  modified: false,
};

const fbLink = "https://www.facebook.com/";
const twLink = "https://twitter.com/";
const lnLink = "https://www.linkedin.com/";

export type SocialBlockHandle = {
  reset: () => void;
}

interface EntitySocialBlockProps  {
  socials?: any,
  onChange?: any,
}

const normalizeUrl = (url: string) => {
  return url.trim().toLowerCase().replace(/\/+$/, "");
}

const isDuplicateSocial = (list: SocialDetails[], itemSocial: SocialDetails, index?: number): boolean => {
  const candidateUrl = normalizeUrl(itemSocial.url);
  return list.some((item, idx) => {
    if ((index !== undefined && idx === index)) {
      return false;
    }
    return (item.type === itemSocial.type && normalizeUrl(item.url) === candidateUrl);
  });
}

const EntitySocialBlock = React.forwardRef<SocialBlockHandle, EntitySocialBlockProps>(({ socials, onChange}, ref  ) => {
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(initialEditModeState);
  const [entitySocial, setEntitySocial] = useState<SocialDetails>(initialSocialDetails);
  const [entitySocialsList, setEntitySocialsList] = useState([]);
  const [editedSocialDetails, setEditedSocialDetails] =
    useState(initialSocialDetails);
  const [socialsForDelete, setSocialForDelete] = useState([]);
  const [errorField, setErrorField] = useState({ url: false, });
  const [errorEditField, setErrorEditField] = useState({ url: false, });
  const [urlTouched, setUrlTouched] = useState(false);

  useEffect(() => {
    setEntitySocialsList(socials);
  }, [dispatch, socials]);

  const handleClearForm = () => {
    /** clear all fields and error statuses **/
    setEditedSocialDetails(initialSocialDetails);
    setEntitySocial(initialSocialDetails);
    setEditMode(initialEditModeState);
    setEntitySocialsList([]);
    setSocialForDelete([]);
    setErrorField({ url: false, })
    setUrlTouched(false);
  };

  const ClearForm = useCallback(() => {
    handleClearForm();
  }, []);
  useImperativeHandle(ref, () => ({
    reset: ClearForm
  }), [ClearForm]);

  const handleChange = (
    _name: string,
    key: string,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    if (key === "type" && event.target.value === "facebook") {
      setUrlTouched(false);
      return setEntitySocial({
        ...entitySocial,
        [key]: event.target.value,
        url: fbLink,
      });
    }
    if (key === "type" && event.target.value === "twitter") {
      setUrlTouched(false);
      return setEntitySocial({
        ...entitySocial,
        [key]: event.target.value,
        url: twLink,
      });
    }
    if (key === "type" && event.target.value === "linkedin") {
      setUrlTouched(false);
      return setEntitySocial({
        ...entitySocial,
        [key]: event.target.value,
        url: lnLink,
      });
    }

    return setEntitySocial({
      ...entitySocial,
      [key]: event.target.value,
    });
  };

  const handleChangeItem = (
    fieldName: string,
    selectedIndex: number,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    if (fieldName === "type" && event.target.value === "facebook") {
      setUrlTouched(false);
      return setEditedSocialDetails({
        ...editedSocialDetails,
        [fieldName]: event.target.value,
        url: fbLink,
        index: selectedIndex,
        modified: true,
      });
    }
    if (fieldName === "type" && event.target.value === "twitter") {
      setUrlTouched(false);
      return setEditedSocialDetails({
        ...editedSocialDetails,
        [fieldName]: event.target.value,
        url: twLink,
        index: selectedIndex,
        modified: true,
      });
    }
    if (fieldName === "type" && event.target.value === "linkedin") {
      setUrlTouched(false);
      return setEditedSocialDetails({
        ...editedSocialDetails,
        [fieldName]: event.target.value,
        url: lnLink,
        index: selectedIndex,
        modified: true,
      });
    }
    setEditedSocialDetails({
      ...editedSocialDetails,
      [fieldName]: event.target.value,
      index: selectedIndex,
      modified: true,
    });
  };

  const handleDeleteItem = (selectedIndex: number) => {
    const newStateForDelete = [];
    entitySocialsList.map((item: any, index) => {
      if (index === selectedIndex && item.id) {
        // @ts-ignore
        return newStateForDelete.push(item);
      } else {
        return item;
      }
    });

    const filteredList = entitySocialsList.filter(
      (_item: any, index) => index !== selectedIndex
    );

    const newState = {
      filteredList,
      newStateForDelete,
    };

    setEntitySocialsList(filteredList);
    setSocialForDelete([...socialsForDelete, ...newStateForDelete]);
    onChange("entity_socials", newState, true);
  };

  const handleSetEditMode = (type: string, selectedIndex: number) => {
    /** managing open/close state for ip item change, save changes by click in socials list **/
    if (type === "open") {
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
      entitySocialsList.forEach((item: any, index: number) => {
        if (index === selectedIndex) {
          setEditedSocialDetails({
            ...item,
            index: selectedIndex,
            type: item.type,
            url: item.url,
          });
        }
      });
    }
    if (type === "close") {
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
      setErrorEditField({ url: false, });
    }
    if (type === "edit") {
      if (!errorEditField.url) {
        if (isDuplicateSocial(entitySocialsList, editedSocialDetails, selectedIndex)) {
          return AlertService.warning("This social link is already added.");
        }
        const newState = entitySocialsList.map((item: any, index: number) => {
          if (index === editedSocialDetails.index && index === selectedIndex) {
            return {
              id: editedSocialDetails.id,
              type: editedSocialDetails.type,
              url: editedSocialDetails.url,
              modified: editedSocialDetails.modified,
            };
          } else {
            return item;
          }
        });
        // @ts-ignore
        setEntitySocialsList(newState);
        onChange("entity_socials", newState);
        setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
        setEditedSocialDetails(editedSocialDetails);
      }

    }
  };

  const addNewSocial = () => {
    const type = entitySocial.type;
    const url = entitySocial.url;

    if (!type) {
      return AlertService.warning("Please enter the type of social network.");
    }
    if (!url) {
        return AlertService.warning("Please enter the link of social network.");
    }
    if (errorField.url) {
      return;
    }
    const itemSocial = { type, url};

    if (isDuplicateSocial(entitySocialsList, itemSocial)) {
      return AlertService.warning("This social link is already added.");
    }
    const newState = [
      ...entitySocialsList,
      itemSocial
          // {
          //   type: entitySocial.type,
          //   url: entitySocial.url,
          // },
    ];
    // @ts-ignore
    setEntitySocialsList(newState);
    onChange("entity_socials", newState);
    handleClearForm();
  };
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography component="legend" sx={styles.textField}>
          Social media
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={styles.fieldHolder}>
          <Box sx={styles.fieldBlock}>
            <FormControl
              fullWidth
              margin="dense"
            >
              <StyledInputLabel
                id="select-social-type-label"
                htmlFor="select-social-type-input"
              >
                Type
              </StyledInputLabel>
              <StyledSelect
                labelId="select-social-type-label"
                id="select-social-type"
                name="selectSocialType"
                value={entitySocial.type ?? ""}
                label="Type"
                onChange={(event: SelectChangeEvent) => handleChange("entity_socials", "type", event)
                }
                inputProps={{
                  id: 'select-social-type-input',
                }}
              >
                {socialLinkOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
            {/*<StyledTextField*/}
            {/*  fullWidth*/}
            {/*  select*/}
            {/*  label="Type"*/}
            {/*  margin="dense"*/}
            {/*  onChange={(event) => handleChange("entity_socials", "type", event)}*/}
            {/*  value={entitySocial.type ?? ""}*/}
            {/*>*/}
            {/*  {socialLinkOptions.map((option) => (*/}
            {/*    <MenuItem key={option.value} value={option.value}>*/}
            {/*      {option.label}*/}
            {/*    </MenuItem>*/}
            {/*  ))}*/}
            {/*</StyledTextField>*/}
          </Box>
          <Box sx={styles.fieldBlock}>
            <LinkTextField
              fullWidth
              label="Link"
              type="text"
              name="url"
              disabled={!entitySocial?.type}
              errorFields={errorField.url}
              setErrorFields={setErrorField}
              touched={urlTouched}
              setTouched={setUrlTouched}
              margin="dense"
              value={entitySocial?.url}
              onChange={(event) => handleChange('entity_socials', 'url', event)}
              platform={toPlatform(entitySocial.type)}
            />
          </Box>
        </Box>
        <Button
          sx={styles.button}
          onClick={addNewSocial}
          disabled={!entitySocial?.type || !entitySocial?.url || errorField?.url}
        >
          Add new social .net
        </Button>
        <Divider sx={{ marginTop: "15px", marginBottom: "15px" }} />
        {entitySocialsList?.length > 0 &&
          entitySocialsList.map(
            (
              item: {
                type: string;
                url: string;
              },
              index
            ) => {
              return editMode.isEdit && editMode.index === index ? (
                <Box sx={styles.editHolder} key={index}>
                  <Typography variant="body2" sx={styles.textField}>
                    Edit current social link
                  </Typography>
                  <Box sx={styles.editFieldsBlockHolder}>
                    <Box sx={styles.fieldBlock}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        variant="standard"
                      >
                        <StyledInputLabel
                          id="select-edit-social-type-label"
                          htmlFor="select-edit-social-type-input"
                          variant="standard"
                        >
                          Type
                        </StyledInputLabel>
                        <StyledSelect
                          labelId="select-edit-social-type-label"
                          id="select-edit-social-type"
                          name="selectEditSocialType"
                          value={editedSocialDetails.type}
                          label="Type"
                          variant="standard"
                          onChange={(event: SelectChangeEvent) => handleChangeItem("type", index, event)
                          }
                          sx={{
                            marginRight: "15px",
                            '& .MuiSelect-icon': {
                              display: 'none',
                            },
                            '& .MuiSelect-select': {
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                              appearance: 'none',
                            },
                          }}
                          inputProps={{
                            id: 'select-edit-social-type-input',
                          }}
                        >
                          {socialLinkOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </StyledSelect>
                      </FormControl>
                      {/*<TextField*/}
                      {/*  fullWidth*/}
                      {/*  select*/}
                      {/*  sx={{*/}
                      {/*    marginRight: "15px",*/}
                      {/*    '& .MuiSelect-icon': {*/}
                      {/*    display: 'none',*/}
                      {/*    },*/}
                      {/*    '& .MuiSelect-select': {*/}
                      {/*    WebkitAppearance: 'none',*/}
                      {/*    MozAppearance: 'none',*/}
                      {/*    appearance: 'none',*/}
                      {/*    },*/}
                      {/*  }}*/}
                      {/*  label="Type"*/}
                      {/*  variant="standard"*/}
                      {/*  onChange={(e) => handleChangeItem("type", index, e)}*/}
                      {/*  value={editedSocialDetails.type}*/}

                      {/*>*/}
                      {/*  {socialLinkOptions.map((option) => (*/}
                      {/*    <MenuItem key={option.value} value={option.value}>*/}
                      {/*      {option.label}*/}
                      {/*    </MenuItem>*/}
                      {/*  ))}*/}
                      {/*</TextField>*/}
                    </Box>
                    <Box sx={styles.fieldBlock}>
                      <LinkTextField
                        fullWidth
                        label="Link"
                        type="text"
                        name="url"
                        required
                        check={true}
                        variant="standard"
                        errorFields={errorEditField.url}
                        setErrorFields={setErrorEditField}
                        value={editedSocialDetails?.url}
                        onChange={(e) => handleChangeItem("url", index, e)}
                        platform={toPlatform(editedSocialDetails.type)}
                      />
                      {/*<TextField*/}
                      {/*  sx={{ width: "100%" }}*/}
                      {/*  label="Link"*/}
                      {/*  variant="standard"*/}
                      {/*  required*/}
                      {/*  onChange={(e) => handleChangeItem("url", index, e)}*/}
                      {/*  value={editedSocialDetails.url}*/}
                      {/*/>*/}
                    </Box>
                  </Box>
                  <Box sx={styles.editButtonGroup}>
                    <IconButton
                      disabled={errorEditField.url}
                      // onMouseDown={(e) => {
                      //   e.preventDefault();
                      // }}
                      onClick={() => handleSetEditMode("edit", index)}
                    >
                      <DoneIcon fontSize={"small"} />
                    </IconButton>
                    <IconButton
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      onClick={() => handleSetEditMode("close", index)}
                    >
                      <CloseIcon fontSize={"small"} />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Box sx={styles.itemContainer} key={index}>
                  <Box sx={styles.itemHolder}>
                    <Box sx={styles.infoHolder}>
                      <Box sx={styles.typeInfo}>
                        <Typography sx={styles.titleDetails} variant={"h6"}>
                          Type:
                        </Typography>
                        <Tooltip
                          title={item?.type}
                          arrow
                          disableInteractive
                        >
                          <Typography sx={styles.itemDetails} variant={"h6"}>
                            {item?.type}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box sx={styles.info}>
                        <Typography sx={styles.titleDetails} variant={"h6"}>
                          Social link:
                        </Typography>
                        <Tooltip
                          title={item?.url}
                          arrow
                          disableInteractive
                        >
                          <Typography sx={styles.itemInfoDetails} variant={"h6"}>
                            {item?.url}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Box sx={styles.iconHolder}>
                      <IconButton
                        aria-label="Edit"
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                        }}
                        onClick={() => handleSetEditMode("open", index)}
                      >
                        <EditIcon sx={styles.editIcon} />
                      </IconButton>
                      <IconButton
                        aria-label="Delete"
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                        }}
                        onClick={() => handleDeleteItem(index)}
                      >
                        <DeleteIcon sx={styles.deleteIcon} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              );
            }
          )}
      </AccordionDetails>
    </Accordion>
  );
}
);

export default EntitySocialBlock;
